import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';

/** Only needs enough of an action to title the dialog, so both the full timeline and the
 *  my-work summary can use it without inventing the fields they don't have. */
export interface CompletableAction {
  id: number;
  subject: string;
  outcome?: string | null;
}

export function CompleteActionDialog({
  action, onConfirm, onClose,
}: {
  action: CompletableAction | null;
  onConfirm: (outcome: string | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState('');

  useEffect(() => {
    if (action) setOutcome(action.outcome ?? '');
  }, [action]);

  return (
    <Dialog open={Boolean(action)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('forms.complete.title', { subject: action?.subject ?? '' })}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          sx={{ mt: 1 }}
          label={t('forms.complete.outcome')}
          multiline
          minRows={2}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          helperText={t('forms.complete.hint')}
          slotProps={{ htmlInput: { dir: 'auto', maxLength: 1000, 'data-testid': 'complete-outcome' } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(outcome.trim() === '' ? null : outcome.trim())}
          data-testid="confirm-complete"
        >
          {t('forms.complete.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
